/** Preview frames default to 16:9; session `size` is separate (see resolveUploadVideoSize). */
export const PREVIEW_CONTAINER_ASPECT_RATIO = 16 / 9

/** CSS `aspect-ratio` string for a session target size — keeps preview layout aligned with passed `size`. */
export function resolvePreviewAspectRatio(size: [number, number]): string {
  return `${size[0]} / ${size[1]}`
}

/** Default landscape session size under the 832 rule (16:9). */
export const DEFAULT_SESSION_TARGET_SIZE: [number, number] = [1472, 832]

const UPLOAD_SIZE_ALIGN = 32
export const MIN_UPLOAD_VIDEO_PIXELS = 600_000
export const MAX_UPLOAD_VIDEO_PIXELS = 1_280_000

function alignToSizeMultiple(value: number): number {
  return Math.max(UPLOAD_SIZE_ALIGN, Math.round(value / UPLOAD_SIZE_ALIGN) * UPLOAD_SIZE_ALIGN)
}

function alignUpToSizeMultiple(value: number): number {
  return Math.max(UPLOAD_SIZE_ALIGN, Math.ceil(value / UPLOAD_SIZE_ALIGN) * UPLOAD_SIZE_ALIGN)
}

function alignDownToSizeMultiple(value: number): number {
  return Math.max(UPLOAD_SIZE_ALIGN, Math.floor(value / UPLOAD_SIZE_ALIGN) * UPLOAD_SIZE_ALIGN)
}

function makeEven(value: number): number {
  const rounded = Math.max(2, Math.round(value))
  return rounded % 2 === 0 ? rounded : rounded - 1
}

/**
 * Upload publish size — keep source resolution (even dimensions for H.264).
 * Avoids canvas downscale blur.
 */
export function resolveUploadPublishSize(sourceWidth: number, sourceHeight: number): [number, number] {
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    return DEFAULT_SESSION_TARGET_SIZE
  }
  return [makeEven(sourceWidth), makeEven(sourceHeight)]
}

/**
 * Adaptive RTC encode/session size.
 * Keeps the base width and scales height for wide sources; tall sources use the full base size.
 */
export function resolveAdaptiveRtcVideoSize(
  sourceWidth: number,
  sourceHeight: number,
  baseSize: [number, number] = DEFAULT_SESSION_TARGET_SIZE,
): [number, number] {
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    return baseSize
  }

  const [baseWidth, baseHeight] = baseSize
  const sourceAspectRatio = sourceHeight / sourceWidth
  const baseAspectRatio = baseHeight / baseWidth

  if (sourceAspectRatio >= baseAspectRatio) {
    return [baseWidth, baseHeight]
  }

  const adaptiveHeight = Math.max(
    UPLOAD_SIZE_ALIGN,
    Math.round(baseWidth * sourceAspectRatio),
  )
  return [baseWidth, makeEven(adaptiveHeight)]
}

/**
 * Upload session size:
 * - below 600k pixels: use the smallest proportional upscale above 600k;
 * - above 1.28m pixels: use the largest proportional downscale below 1.28m;
 * - inside the range: keep native scale.
 * Width and height are aligned to multiples of 32 in the scale direction.
 */
export function resolveUploadVideoSize(sourceWidth: number, sourceHeight: number): [number, number] {
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    return DEFAULT_SESSION_TARGET_SIZE
  }

  const sourcePixels = sourceWidth * sourceHeight
  let size: [number, number]

  if (sourcePixels < MIN_UPLOAD_VIDEO_PIXELS) {
    const scale = Math.sqrt(MIN_UPLOAD_VIDEO_PIXELS / sourcePixels)
    size = [
      alignUpToSizeMultiple(sourceWidth * scale),
      alignUpToSizeMultiple(sourceHeight * scale),
    ]
  } else if (sourcePixels > MAX_UPLOAD_VIDEO_PIXELS) {
    const scale = Math.sqrt(MAX_UPLOAD_VIDEO_PIXELS / sourcePixels)
    size = [
      alignDownToSizeMultiple(sourceWidth * scale),
      alignDownToSizeMultiple(sourceHeight * scale),
    ]
  } else {
    size = [
      alignToSizeMultiple(sourceWidth),
      alignToSizeMultiple(sourceHeight),
    ]
  }

  const alignedPixels = size[0] * size[1]
  if (alignedPixels <= MIN_UPLOAD_VIDEO_PIXELS) {
    const scale = Math.sqrt(MIN_UPLOAD_VIDEO_PIXELS / sourcePixels)
    return [
      alignUpToSizeMultiple(sourceWidth * scale),
      alignUpToSizeMultiple(sourceHeight * scale),
    ]
  }
  if (alignedPixels >= MAX_UPLOAD_VIDEO_PIXELS) {
    const scale = Math.sqrt(MAX_UPLOAD_VIDEO_PIXELS / sourcePixels)
    const constrained: [number, number] = [
      alignDownToSizeMultiple(sourceWidth * scale),
      alignDownToSizeMultiple(sourceHeight * scale),
    ]
    if (constrained[0] * constrained[1] >= MAX_UPLOAD_VIDEO_PIXELS) {
      if (constrained[0] >= constrained[1]) {
        constrained[0] = Math.max(UPLOAD_SIZE_ALIGN, constrained[0] - UPLOAD_SIZE_ALIGN)
      } else {
        constrained[1] = Math.max(UPLOAD_SIZE_ALIGN, constrained[1] - UPLOAD_SIZE_ALIGN)
      }
    }
    return constrained
  }
  return size
}

export function resolveSessionTargetSize(input: {
  localVideoSize?: { width: number; height: number } | null
  overrideSize?: [number, number]
}): [number, number] {
  if (input.overrideSize) {
    return input.overrideSize
  }

  if (input.localVideoSize?.width && input.localVideoSize?.height) {
    return [input.localVideoSize.width, input.localVideoSize.height]
  }

  return DEFAULT_SESSION_TARGET_SIZE
}
